import React from 'react';
import { Pressable, StyleSheet, ViewStyle, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { Ionicons } from '@expo/vector-icons';
import { addColorOpacity } from '../../shared/utils/styles';

export interface IconButtonProps {
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  iconColor?: string;
}

export function IconButton({
  icon,
  onPress,
  variant = 'ghost',
  size = 'md',
  disabled = false,
  loading = false,
  style,
  iconColor,
}: IconButtonProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const variantStyle = styles[variant];
  const sizeKey = `size${size.charAt(0).toUpperCase() + size.slice(1)}` as 'sizeSm' | 'sizeMd' | 'sizeLg';
  const sizeStyle = styles[sizeKey];
  const disabledStyle = (disabled || loading) ? styles.disabled : null;

  const defaultIconColor = variant === 'primary'
    ? tokens.color.onPrimary
    : variant === 'secondary'
      ? tokens.color.onSecondary
      : variant === 'danger'
        ? tokens.color.onError
        : variant === 'outline'
          ? tokens.color.onSurface
          : tokens.color.onSurface;

  const finalIconColor = iconColor || defaultIconColor;
  const iconSize = size === 'sm' ? 16 : size === 'md' ? 20 : 24;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.base,
        variantStyle,
        sizeStyle,
        disabledStyle,
        pressed && !disabled && !loading && styles.pressed,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator 
          size="small" 
          color={finalIconColor} 
        />
      ) : (
        <Ionicons 
          name={icon} 
          size={iconSize} 
          color={finalIconColor} 
        />
      )}
    </Pressable>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    base: {
      borderRadius: utils.radius.md,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: {
          cursor: 'pointer',
        },
      }),
    },
    // Sizes
    sizeSm: {
      width: 32,
      height: 32,
      minWidth: 32,
      minHeight: 32,
    },
    sizeMd: {
      width: 44,
      height: 44,
      minWidth: 44,
      minHeight: 44,
    },
    sizeLg: {
      width: 56,
      height: 56,
      minWidth: 56,
      minHeight: 56,
    },
    // Variants
    primary: {
      backgroundColor: tokens.color.primary,
    },
    secondary: {
      backgroundColor: tokens.color.secondary,
    },
    danger: {
      backgroundColor: tokens.color.error,
    },
    outline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    ghost: {
      backgroundColor: 'transparent',
    },
    disabled: {
      opacity: 0.6,
    },
    pressed: {
      opacity: 0.8,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
    },
  });
}

